export type QuestRoute =
  | { kind: "new" }
  | { kind: "edit"; id: string }
  | { kind: "route"; id: string }
  | { kind: "view"; id: string };

const ID = "([A-Za-z0-9-]{1,64})";
const QUEST_ID = new RegExp(`^${ID.slice(1, -1)}$`);

/**
 * Match `/quest/new`, `/quest/:id/edit`, `/quest/:id/route` (the map editor bound to
 * the quest's route), and public `/q/:id`. `/quests/...` is accepted as an alias.
 */
export function matchQuestRoute(pathname: string): QuestRoute | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/quest/new" || path === "/quests/new") return { kind: "new" };
  const owned = path.match(new RegExp(`^/quests?/${ID}/(edit|route)$`));
  if (owned) return { kind: owned[2] as "edit" | "route", id: owned[1]! };
  const view = path.match(new RegExp(`^/q/${ID}$`));
  if (view) return { kind: "view", id: view[1]! };
  return null;
}

export function questPath(route: QuestRoute): string {
  if (route.kind === "new") return "/quest/new";
  if (route.kind === "view") return questViewPath(route.id);
  return `/quest/${encodeURIComponent(route.id)}/${route.kind}`;
}

export function questEditPath(id: string): string {
  return questPath({ kind: "edit", id });
}

export function questRoutePath(id: string): string {
  return questPath({ kind: "route", id });
}

export function questViewPath(id: string): string {
  return `/q/${encodeURIComponent(id)}`;
}

/** Public quest page for the home detail panel; drafts stay unlinked. */
export function publishedQuestViewHref(q: { id: string; status: string }): string | null {
  if (q.status === "draft") return null;
  return questViewPath(q.id);
}

/**
 * Where an entry URL belongs: an alias path or the old `/?quest=<id>[&piece=…]`
 * editor link moves to the canonical quest path. Other query params (e.g. `lang`)
 * are kept. Null when the URL is already canonical or isn't a quest URL.
 */
export function canonicalQuestUrl(pathname: string, search: string): string | null {
  const params = new URLSearchParams(search);
  let route = matchQuestRoute(pathname);
  if (!route && (pathname === "/" || pathname === "")) {
    const legacy = params.get("quest");
    if (legacy && QUEST_ID.test(legacy)) route = { kind: "route", id: legacy };
  }
  if (!route) return null;
  params.delete("quest");
  if (route.kind === "route") params.delete("piece");
  const query = params.toString();
  const next = `${questPath(route)}${query ? `?${query}` : ""}`;
  return next === `${pathname}${search}` ? null : next;
}
